import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, HandCoins, MessageCircle, Phone, Search, Users, Wallet } from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/data";
import { useSettings } from "@/context/SettingsContext";
import { telHref, whatsappHref } from "../../enquiries/enquiry";
import {
  Card, ExportButtons, Kpi, KpiGrid, Note, ReportBody, ReportHeader, TableScroll,
  money, th, td, useReport, type ExportSpec,
} from "../ReportKit";

// ─── Types ───────────────────────────────────────────────────────────────────

type BucketKey = "not_due" | "late_1_30" | "late_31_60" | "late_61_90" | "late_90_plus";

interface EventRef { id: number; name: string; event_type: string | null; status: string; start_date: string | null; end_date: string | null }
interface ClientRef { id: number; name: string; phone: string | null }

interface OwedRow {
  event: EventRef;
  client: ClientRef | null;
  invoice: { id: number; number: string } | null;
  billed?: number;
  received: number;
  balance: number;
  due_date: string | null;
  days_late: number;
  bucket: BucketKey;
  last_payment_date: string | null;
}

interface ClientOwed { id: number | null; name: string; phone: string | null; events: number; owed: number; late: number; most_days_late: number }

interface MoneyOwed {
  can_view_money: boolean;
  as_of: string;
  summary: { owed: number; events: number; clients: number; late: number; late_events: number; not_due: number };
  buckets: { key: BucketKey; label: string; amount: number; events: number }[];
  events: OwedRow[];
  clients: ClientOwed[];
  drafts: { events: number; amount: number };
  cancelled_with_payments: {
    events: number;
    amount: number;
    list: { event: EventRef; client: ClientRef | null; received: number; last_payment_date: string | null }[];
  };
}

// Colour per age group: calm while not due, redder the later it gets
const BUCKET_STYLE: Record<BucketKey, { bar: string; chip: string; dot: string }> = {
  not_due: { bar: "bg-sky-400", chip: "bg-sky-50 text-sky-700 border-sky-200", dot: "bg-sky-400" },
  late_1_30: { bar: "bg-amber-400", chip: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-400" },
  late_31_60: { bar: "bg-orange-500", chip: "bg-orange-50 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  late_61_90: { bar: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  late_90_plus: { bar: "bg-red-700", chip: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-700" },
};

const lateText = (r: { days_late: number; due_date: string | null }) =>
  r.days_late > 0 ? `${r.days_late} day${r.days_late === 1 ? "" : "s"} late` : r.due_date ? `Due ${formatDate(r.due_date)}` : "No date";

const eventDates = (e: EventRef) =>
  !e.start_date ? "—" : e.end_date && e.end_date !== e.start_date ? `${formatDate(e.start_date)} – ${formatDate(e.end_date)}` : formatDate(e.start_date);

/** WhatsApp link with a polite reminder already typed in. */
function reminderHref(phone: string | null | undefined, text: string): string {
  const base = phone ? whatsappHref(phone) : "";
  return base ? `${base}?text=${encodeURIComponent(text)}` : "";
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function MoneyOwedPage() {
  // No date range: this report is always "as of today"
  const state = useReport<MoneyOwed>(async () => (await reportsApi.receivables()).data, []);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={HandCoins}
          iconClass="text-rose-600"
          title="Money Owed"
          subtitle={state.data ? `What clients still have to pay, as of ${formatDate(state.data.as_of)}` : "What clients still have to pay, and how late it is"}
        >
          {state.status !== 403 && (
            <ExportButtons build={() => state.data && buildExport(state.data)} disabled={state.loading || !state.data} />
          )}
        </ReportHeader>

        <ReportBody state={state}>
          {(d) => <MoneyOwedView d={d} />}
        </ReportBody>
      </div>
    </DashboardLayout>
  );
}

function MoneyOwedView({ d }: { d: MoneyOwed }) {
  const { settings } = useSettings();
  const business = settings.business?.businessName || "us";
  const [view, setView] = useState<"events" | "clients">("events");
  const [bucket, setBucket] = useState<BucketKey | "all">("all");
  const [query, setQuery] = useState("");
  const s = d.summary;

  const q = query.trim().toLowerCase();
  const matches = (...parts: (string | null | undefined)[]) => !q || parts.some((p) => (p || "").toLowerCase().includes(q));

  const events = useMemo(
    () => d.events.filter((r) => (bucket === "all" || r.bucket === bucket) && matches(r.client?.name, r.client?.phone, r.event.name, r.invoice?.number)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [d.events, bucket, q],
  );
  const clients = useMemo(
    () => d.clients.filter((c) => matches(c.name, c.phone)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [d.clients, q],
  );

  const eventReminder = (r: OwedRow) =>
    `Namaste ${r.client?.name ?? ""}, this is a gentle reminder from ${business} that ${money(r.balance)} is pending for ${r.event.name}` +
    `${r.event.start_date ? ` (${formatDate(r.event.start_date)})` : ""}. Please let us know when we can expect the payment. Thank you!`;
  const clientReminder = (c: ClientOwed) =>
    `Namaste ${c.name}, this is a gentle reminder from ${business} that ${money(c.owed)} is pending with us` +
    `${c.events > 1 ? ` for ${c.events} bookings` : ""}. Please let us know when we can expect the payment. Thank you!`;

  const oldest = d.events[0];

  return (
    <>
      <KpiGrid>
        <Kpi label="Total owed" icon={Wallet} tone="stone" value={money(s.owed)} sub={`${s.events} event${s.events === 1 ? "" : "s"} · ${s.clients} client${s.clients === 1 ? "" : "s"}`} />
        <Kpi label="Late" icon={AlertTriangle} tone="red" valueClass="text-red-700" value={money(s.late)} sub={`${s.late_events} event${s.late_events === 1 ? "" : "s"} past their date`} />
        <Kpi label="Not due yet" icon={CalendarClock} tone="blue" value={money(s.not_due)} sub="Events today or still to come" />
        <Kpi
          label="Waiting longest" icon={Users} tone="amber"
          value={oldest && oldest.days_late > 0 ? `${oldest.days_late} days` : "—"}
          sub={oldest && oldest.days_late > 0 ? oldest.client?.name ?? oldest.event.name : "Nothing is late"}
        />
      </KpiGrid>

      {/* How late: one bar split by age group, and a tile per group that filters the list */}
      <Card title="How late is the money?" subtitle="An event is due on its last day, same as “Overdue” on the dashboard">
        {s.owed > 0 && (
          <div className="flex h-3 rounded-full overflow-hidden bg-stone-100 mb-3" aria-hidden="true">
            {d.buckets.filter((b) => b.amount > 0).map((b) => (
              <div key={b.key} className={BUCKET_STYLE[b.key].bar} style={{ width: `${(b.amount / s.owed) * 100}%` }} />
            ))}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <BucketTile active={bucket === "all"} onClick={() => setBucket("all")} label="All" amount={s.owed} events={s.events} dot="bg-stone-400" />
          {d.buckets.map((b) => (
            <BucketTile
              key={b.key}
              active={bucket === b.key}
              onClick={() => { setBucket(b.key); setView("events"); }}
              label={b.label}
              amount={b.amount}
              events={b.events}
              dot={BUCKET_STYLE[b.key].dot}
            />
          ))}
        </div>
      </Card>

      <Card
        title={view === "events" ? "By event" : "By client"}
        actions={
          <div className="inline-flex rounded-xl border border-stone-200 bg-stone-50 p-0.5" role="tablist">
            {(["events", "clients"] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className={`h-9 px-3 rounded-lg text-sm font-medium ${view === v ? "bg-white shadow-sm text-stone-800" : "text-stone-500"}`}
              >
                {v === "events" ? `Events (${events.length})` : `Clients (${clients.length})`}
              </button>
            ))}
          </div>
        }
      >
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search client, phone, event or invoice"
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400"
          />
        </div>

        {view === "events" ? (
          events.length === 0 ? (
            <Empty text={d.events.length === 0 ? "Nobody owes anything right now." : "Nothing matches."} />
          ) : (
            <>
              {/* Phones: one card per event */}
              <div className="md:hidden space-y-2">
                {events.map((r) => (
                  <div key={r.event.id} className="rounded-xl border border-stone-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-800 truncate">{r.client?.name ?? "No client"}</p>
                        <Link to={`/events/detail?id=${r.event.id}`} className="text-xs text-stone-500 hover:text-amber-700 block truncate">
                          {r.event.name} · {eventDates(r.event)}
                        </Link>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold tabular-nums text-stone-800">{money(r.balance)}</p>
                        <span className={`inline-block mt-0.5 text-[11px] px-2 py-0.5 rounded-full border ${BUCKET_STYLE[r.bucket].chip}`}>{lateText(r)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-stone-500 mt-2">
                      Paid {money(r.received)}{r.billed !== undefined ? ` of ${money(r.billed)}` : ""}
                      {r.last_payment_date ? ` · last on ${formatDate(r.last_payment_date)}` : " · nothing paid yet"}
                    </p>
                    <ContactButtons phone={r.client?.phone} reminder={eventReminder(r)} eventId={r.event.id} />
                  </div>
                ))}
              </div>

              {/* Larger screens: table */}
              <div className="hidden md:block">
                <TableScroll minWidth={880}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 text-left">
                        <th className={th}>Client</th>
                        <th className={th}>Event</th>
                        <th className={th}>Invoice</th>
                        {d.can_view_money && <th className={`${th} text-right`}>Billed</th>}
                        <th className={`${th} text-right`}>Paid</th>
                        <th className={`${th} text-right`}>Owed</th>
                        <th className={th}>How late</th>
                        <th className={th}>Last payment</th>
                        <th className={`${th} text-right`}>Contact</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {events.map((r) => (
                        <tr key={r.event.id} className="hover:bg-stone-50/60">
                          <td className={td}>
                            <p className="font-medium text-stone-800">{r.client?.name ?? "No client"}</p>
                            {r.client?.phone && <p className="text-xs text-stone-500">{r.client.phone}</p>}
                          </td>
                          <td className={td}>
                            <Link to={`/events/detail?id=${r.event.id}`} className="font-medium text-stone-700 hover:text-amber-700">{r.event.name}</Link>
                            <p className="text-xs text-stone-500">{eventDates(r.event)}</p>
                          </td>
                          <td className={`${td} text-stone-600`}>{r.invoice?.number ?? "—"}</td>
                          {d.can_view_money && <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.billed)}</td>}
                          <td className={`${td} text-right tabular-nums text-stone-600`}>{money(r.received)}</td>
                          <td className={`${td} text-right tabular-nums font-semibold text-stone-800`}>{money(r.balance)}</td>
                          <td className={td}>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${BUCKET_STYLE[r.bucket].chip}`}>{lateText(r)}</span>
                          </td>
                          <td className={`${td} text-stone-500`}>{r.last_payment_date ? formatDate(r.last_payment_date) : "Nothing yet"}</td>
                          <td className={`${td} text-right`}>
                            <ContactButtons phone={r.client?.phone} reminder={eventReminder(r)} eventId={r.event.id} compact />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                        <td className={td} colSpan={d.can_view_money ? 4 : 3}>Total ({events.length})</td>
                        <td className={`${td} text-right tabular-nums`}>{money(events.reduce((a, r) => a + r.received, 0))}</td>
                        <td className={`${td} text-right tabular-nums`}>{money(events.reduce((a, r) => a + r.balance, 0))}</td>
                        <td className={td} colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </TableScroll>
              </div>
            </>
          )
        ) : clients.length === 0 ? (
          <Empty text={d.clients.length === 0 ? "Nobody owes anything right now." : "Nothing matches."} />
        ) : (
          <div className="divide-y divide-stone-100">
            {clients.map((c) => (
              <div key={c.id ?? c.name} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-800 truncate">{c.name}</p>
                  <p className="text-xs text-stone-500">
                    {c.events} event{c.events === 1 ? "" : "s"}
                    {c.phone ? ` · ${c.phone}` : ""}
                    {c.most_days_late > 0 ? ` · oldest ${c.most_days_late} days late` : ""}
                  </p>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-4">
                  <div className="text-left sm:text-right">
                    <p className="font-bold tabular-nums text-stone-800">{money(c.owed)}</p>
                    {c.late > 0 && <p className="text-xs text-red-600 tabular-nums">{money(c.late)} late</p>}
                  </div>
                  <ContactButtons phone={c.phone} reminder={clientReminder(c)} compact />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {(d.drafts.events > 0 || d.cancelled_with_payments.events > 0) && (
        <div className="grid gap-3 md:grid-cols-2">
          {d.drafts.events > 0 && (
            <Card title="Draft events (not counted)">
              <p className="text-sm text-stone-700">
                <span className="font-semibold">{d.drafts.events}</span> draft event{d.drafts.events === 1 ? "" : "s"} worth{" "}
                <span className="font-semibold">{money(d.drafts.amount)}</span> still unpaid.
              </p>
              <Note>They count as money owed once the booking is confirmed.</Note>
            </Card>
          )}
          {d.cancelled_with_payments.events > 0 && (
            <Card title="Advances on cancelled bookings" subtitle="Money received for events that were later cancelled">
              <p className="text-sm text-stone-700 mb-2">
                <span className="font-semibold">{money(d.cancelled_with_payments.amount)}</span> across {d.cancelled_with_payments.events} event{d.cancelled_with_payments.events === 1 ? "" : "s"}.
              </p>
              <ul className="divide-y divide-stone-100 text-sm">
                {d.cancelled_with_payments.list.map((k) => (
                  <li key={k.event.id} className="py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <Link to={`/events/detail?id=${k.event.id}`} className="font-medium text-stone-700 hover:text-amber-700 truncate block">{k.event.name}</Link>
                      <p className="text-xs text-stone-500 truncate">{k.client?.name ?? "No client"} · {eventDates(k.event)}</p>
                    </div>
                    <span className="tabular-nums font-semibold text-stone-800 shrink-0">{money(k.received)}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}

      <Note>
        Money owed = what confirmed, in-progress and completed events were billed (including GST) minus what the client has paid.
        Draft events count once confirmed; cancelled events owe nothing. Tap “Remind” to open WhatsApp with a polite reminder ready to send.
      </Note>
    </>
  );
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function BucketTile({ active, onClick, label, amount, events, dot }: {
  active: boolean; onClick: () => void; label: string; amount: number; events: number; dot: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-left rounded-xl border p-2.5 min-h-10 transition-colors ${active ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : "border-stone-200 hover:bg-stone-50"}`}
    >
      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-stone-500">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        {label}
      </span>
      <span className="block text-sm font-bold tabular-nums text-stone-800 mt-0.5">{money(amount)}</span>
      <span className="block text-[11px] text-stone-500">{events} event{events === 1 ? "" : "s"}</span>
    </button>
  );
}

function ContactButtons({ phone, reminder, eventId, compact = false }: {
  phone: string | null | undefined; reminder: string; eventId?: number; compact?: boolean;
}) {
  const tel = phone ? telHref(phone) : "";
  const wa = reminderHref(phone, reminder);
  const btn = "h-10 inline-flex items-center justify-center gap-1.5 rounded-xl border text-sm font-medium";
  return (
    <div className={`flex gap-2 ${compact ? "justify-end" : "mt-3"}`}>
      {tel && (
        <a href={tel} className={`${btn} border-stone-200 text-stone-700 hover:bg-stone-50 ${compact ? "w-10" : "flex-1"}`} title="Call" aria-label="Call">
          <Phone className="w-4 h-4" />{!compact && "Call"}
        </a>
      )}
      {wa && (
        <a href={wa} target="_blank" rel="noopener noreferrer" className={`${btn} border-emerald-200 text-emerald-700 hover:bg-emerald-50 ${compact ? "w-10" : "flex-1"}`} title="Send a WhatsApp reminder" aria-label="Send a WhatsApp reminder">
          <MessageCircle className="w-4 h-4" />{!compact && "Remind"}
        </a>
      )}
      {eventId !== undefined && !compact && (
        <Link to={`/events/detail?id=${eventId}`} className={`${btn} flex-1 border-amber-200 bg-amber-600 text-white hover:bg-amber-700`}>
          Take payment
        </Link>
      )}
      {!tel && !wa && compact && <span className="text-xs text-stone-400">No phone</span>}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-sm text-stone-400 py-10 text-center">{text}</p>;
}

// ─── Excel / Print ───────────────────────────────────────────────────────────

function buildExport(d: MoneyOwed): ExportSpec {
  const bucketLabel = Object.fromEntries(d.buckets.map((b) => [b.key, b.label]));
  const money_ = d.can_view_money;
  return {
    title: "Money Owed",
    fileName: "money-owed",
    summary: [
      { label: `As of`, value: d.as_of, type: "date" },
      { label: "Total owed", value: d.summary.owed, type: "money" },
      { label: "Late", value: d.summary.late, type: "money" },
      { label: "Not due yet", value: d.summary.not_due, type: "money" },
      { label: "Events owing", value: d.summary.events, type: "number" },
      { label: "Clients owing", value: d.summary.clients, type: "number" },
      ...d.buckets.map((b) => ({ label: b.label, value: b.amount, type: "money" as const })),
      { label: "Draft events not counted", value: d.drafts.amount, type: "money" },
      { label: "Advances on cancelled bookings", value: d.cancelled_with_payments.amount, type: "money" },
    ],
    tables: [
      {
        title: "By event",
        columns: [
          { label: "Client" }, { label: "Phone" }, { label: "Event" }, { label: "Event date", type: "date" }, { label: "Invoice" },
          ...(money_ ? [{ label: "Billed", type: "money" as const }] : []),
          { label: "Paid", type: "money" }, { label: "Owed", type: "money" },
          { label: "Days late", type: "number" }, { label: "Age group" }, { label: "Last payment", type: "date" },
        ],
        rows: d.events.map((r) => [
          r.client?.name ?? "", r.client?.phone ?? "", r.event.name, r.event.start_date, r.invoice?.number ?? "",
          ...(money_ ? [r.billed ?? 0] : []),
          r.received, r.balance, r.days_late, bucketLabel[r.bucket] ?? r.bucket, r.last_payment_date,
        ]),
        totals: [
          "Total", "", "", "", "",
          ...(money_ ? [d.events.reduce((a, r) => a + (r.billed ?? 0), 0)] : []),
          d.events.reduce((a, r) => a + r.received, 0), d.summary.owed, "", "", "",
        ],
      },
      {
        title: "By client",
        columns: [
          { label: "Client" }, { label: "Phone" }, { label: "Events", type: "number" },
          { label: "Owed", type: "money" }, { label: "Late", type: "money" }, { label: "Oldest (days late)", type: "number" },
        ],
        rows: d.clients.map((c) => [c.name, c.phone ?? "", c.events, c.owed, c.late, c.most_days_late]),
        totals: ["Total", "", d.summary.events, d.summary.owed, d.summary.late, ""],
      },
      ...(d.cancelled_with_payments.list.length ? [{
        title: "Cancelled with payments",
        columns: [{ label: "Event" }, { label: "Event date", type: "date" as const }, { label: "Client" }, { label: "Received", type: "money" as const }, { label: "Last payment", type: "date" as const }],
        rows: d.cancelled_with_payments.list.map((k) => [k.event.name, k.event.start_date, k.client?.name ?? "", k.received, k.last_payment_date]),
        totals: ["Total", "", "", d.cancelled_with_payments.amount, ""],
      }] : []),
    ],
    notes: [
      "Money owed = billed on confirmed, in-progress and completed events (incl. GST) minus what the client has paid. An event is due on its last day.",
      "Draft events count once confirmed; cancelled events owe nothing.",
    ],
  };
}
